// selectors
import { getActiveScriptType } from 'current/selectors';
import { getDirectionToStep } from 'script-flow/selectors';
import { getDefaultDateForActivityType } from 'activities/selectors';
import {
  getDefaultScriptName,
  getPlantingPlanParams
} from 'planting-plan-form/selectors';
import { getSupportInfo } from 'user/selectors';
import { getSelectedManagementZonesType } from 'manual/selectors';

// constants
import { activityTypes } from 'constants/activities';
import { errorLevel } from 'constants/errors';
import { nav } from 'constants/scriptFlow';
import { scriptTypes, managementZonesTypes } from 'constants/scripts';

// actions
import { treatErrorAs } from 'api/errors';
import { lockPlantingPlan, unlockPlantingPlan } from 'current/uiwidgets/locks';
import { resetManualScriptParams, setManualScriptParams, resetGeneratedZones } from 'manual';
import { initializePlantingPlanParams, resetPlantingPlanParams } from 'planting-plan-form';
import { fetchNewManualScript } from 'scripts';
import { getCreateRoute, getManualScriptingRoute } from '../util/routes';

import moment from 'moment';

// scaffolding for script flow actions
// TODO: CFVWEB-2340

const constructTitleString = (scriptType, selectedManagementZonesType, stepNumber) => {
  const scriptDescription = scriptType ? `-${scriptType}` : '';
  const selectedManagementZonesTypeDescription 
    = selectedManagementZonesType ? `-${selectedManagementZonesType}`: '';
  return (
    `title-text__create-manualv2${scriptDescription}`
    + `-script${selectedManagementZonesTypeDescription}--step-${stepNumber}`
  );
};

export const determineV2TitleTextMessageKey = ({ state, stepNumber }) => {
  const scriptType = getActiveScriptType(state);
  const selectedManagementZonesType = getSelectedManagementZonesType(state);
  switch(selectedManagementZonesType) {
    case managementZonesTypes.GENERATE_ZONES:
      if(stepNumber < 4) {
        return `title-text__create-manualv2-generate-zones--step-${stepNumber}`;
      } 
      return constructTitleString(scriptType, selectedManagementZonesType, stepNumber);
    default:
      return constructTitleString(scriptType, selectedManagementZonesType, stepNumber);
  }

};

export const determineTitleTextMessageKey = ({ state, stepNumber }) => {
  if (stepNumber > 1) {
    const selectedManagementZonesType = getSelectedManagementZonesType(state);
    switch(selectedManagementZonesType) {
      case managementZonesTypes.PREVIOUS_PRESCRIPTION:
      case managementZonesTypes.GENERATE_ZONES:
      case managementZonesTypes.IMPORTED_MAPS_PARENT:
        return determineV2TitleTextMessageKey({ state, stepNumber });
      default:
        break;
    }
  }
  const scriptType = getActiveScriptType(state);
  const scriptDescription = scriptType ? `-${scriptType}` : '';
  return `title-text__create-manual${scriptDescription}-script--step-${stepNumber}`;
};

export const onSelectScriptTypeOptions = (dispatch, getState) => {
  dispatch(resetManualScriptParams());
  dispatch(resetPlantingPlanParams());
  return { route: getCreateRoute(getState()) };
};

export const onSelectManagementZones = (dispatch, getState) => {
  const state = getState();
  const direction = getDirectionToStep(state, 1);

  if (direction === nav.BACK) {
    dispatch(resetGeneratedZones());
    dispatch(resetPlantingPlanParams());
  }

  const route = getManualScriptingRoute(state, 1);
  return {
    route,
    titleTextMessageKey: determineTitleTextMessageKey({ state, stepNumber: 1 })
  };
};

export const onSelectPreviousScript = (dispatch, getState) => {
  const state = getState();
  const direction = getDirectionToStep(state, 2);

  if (direction === nav.BACK) {
    dispatch(resetPlantingPlanParams());
  }

  const route = getManualScriptingRoute(state, 2);
  return {route, titleTextMessageKey: determineTitleTextMessageKey({ state, stepNumber: 2 }) };
};

export const onEnterGenerateZones = (dispatch, getState) => {
  const state = getState();

  const direction = getDirectionToStep(state, 2);
  dispatch(resetGeneratedZones());

  if (direction === nav.BACK) {
    dispatch(resetPlantingPlanParams());
  }

  const route = getManualScriptingRoute(state, 2);
  return {route, titleTextMessageKey: determineTitleTextMessageKey({ state, stepNumber: 2 }) };
};

export const onEnterZonesReview = (dispatch, getState) => {
  const state = getState();

  const direction = getDirectionToStep(state, 3);

  if (direction === nav.BACK) {
    dispatch(resetPlantingPlanParams());
  }

  const route = getManualScriptingRoute(state, 3);
  return {route, titleTextMessageKey: determineTitleTextMessageKey({ state, stepNumber: 3 }) };
};

export const onEnterPlantingPlan = (dispatch, getState) => {
  const state = getState();
  const scriptType = getActiveScriptType(state);
  const selectedManagementZonesType = getSelectedManagementZonesType(state);
  let toStepNumber;
  
  switch(selectedManagementZonesType) {
    case managementZonesTypes.PREVIOUS_PRESCRIPTION:
    case managementZonesTypes.IMPORTED_MAPS_PARENT:
      toStepNumber = 3;
      break;
    case managementZonesTypes.GENERATE_ZONES:
      toStepNumber = 4;
      break;
    default:
      toStepNumber = 2;
      break;
  }

  let plantingPlanParams = getPlantingPlanParams(state);

  const defaultScriptName = getDefaultScriptName(state);

  const defaultDate
    = scriptType === scriptTypes.APPLICATION
      ? moment(new Date()).format('YYYY-MM-DD')
        + moment(new Date()).format('THH:mm:ss.SSS')
        + 'Z'
      : getDefaultDateForActivityType(
        state,
        scriptType === scriptTypes.PLANTING
          ? activityTypes.PLANTING
          : activityTypes.FERTILITY_FALL
      );

  plantingPlanParams = {
    ...plantingPlanParams,
    date: defaultDate,
    defaultScriptName
  };
  if (scriptType === scriptTypes.PLANTING) {
    plantingPlanParams = {
      ...plantingPlanParams,
      activityCategoryUuid: activityTypes.PLANTING,
      activityType: activityTypes.PLANTING
    };
  }

  if (scriptType === scriptTypes.APPLICATION) {
    plantingPlanParams = {
      ...plantingPlanParams,
      activityCategoryUuid: activityTypes.APPLICATION,
      activityType: activityTypes.APPLICATION
    };
  }

  dispatch(initializePlantingPlanParams({ plantingPlanParams }));
  dispatch(setManualScriptParams(plantingPlanParams));

  const route = getManualScriptingRoute(state, toStepNumber);
  return {route, titleTextMessageKey: determineTitleTextMessageKey({ state, stepNumber: toStepNumber }) };
};

export const onFinishFlow = (dispatch, getState) => {
  dispatch(lockPlantingPlan());
  fetchNewManualScript()(dispatch, getState)
  .catch((error) => {
    dispatch(unlockPlantingPlan());
    const messageKey
        = error.message === 'Point is not supported for area calculation'
          ? 'help-text__failure--incorrect--shape--file'
          : 'help-text__failure--create-prescription';
    treatErrorAs({
      severity: errorLevel.WARN,
      andHandleNow: true,
      andStopHere: true,
      messageKey: messageKey,
      messageParams: getSupportInfo(getState()),
      convertErrorTo: error
    })(error);
  })
  .then(() => {
    // Ideally, it should be a finally() in the above line,
    // however the build server uses Node 8 which does not support finally()
    dispatch(unlockPlantingPlan());
  });
};
